// Route wrapper for /friends — resolves the bearer's own `friend_code` from
// `GET /api/profile/{self}` (self-only field per DEC-002 / T-15-02) and
// passes it to FriendsScreen as `ownFriendCode`, which lights up the
// "Share my code" header CTA + ShareMyCodeSheet (code + link + QR).
//
// FriendsScreen stays presentational — the prop is the contract. This
// wrapper owns the data fetch + cache reuse (profileCache 60s TTL is
// shared with ProfileScreen, so navigating between /profile/{self} and
// /friends does not re-fetch within the window).
//
// On any error, `ownFriendCode` stays undefined → share CTA stays hidden
// gracefully (the existing fallback in FriendsScreen).

import { useStore } from '@nanostores/react'
import { type JSX, useEffect, useState } from 'react'

import { fetchProfileById } from '../../net/profile.js'
import { $authBearer } from '../../stores/auth.js'
import { initLobbyPolling } from '../../stores/lobbyData.js'
import {
  getCachedProfile,
  setCachedProfile,
} from '../../stores/profileCache.js'
import { FriendsScreen } from './FriendsScreen.js'

export function FriendsRoute(): JSX.Element {
  const bearer = useStore($authBearer)
  const userId = bearer?.user_uuid

  const [ownFriendCode, setOwnFriendCode] = useState<string | undefined>(() => {
    if (!userId) return undefined
    return getCachedProfile(userId)?.friend_code ?? undefined
  })

  useEffect(() => {
    if (!userId) {
      setOwnFriendCode(undefined)
      return
    }
    const cached = getCachedProfile(userId)
    if (cached?.friend_code) {
      setOwnFriendCode(cached.friend_code)
      return
    }
    let cancelled = false
    void fetchProfileById(userId)
      .then((data) => {
        if (cancelled) return
        setCachedProfile(userId, data)
        setOwnFriendCode(data.friend_code ?? undefined)
      })
      .catch(() => undefined)
    return (): void => {
      cancelled = true
    }
  }, [userId])

  // Reuse the existing visibility-aware /api/home poll while on /friends.
  // Refcounted (WR-07) — coexists safely with the lobby's own subscription.
  // FriendsScreen subscribes to $lobbyData and refetches inbox/list/outbox
  // only when counts actually change → zero extra D1 traffic at steady state.
  useEffect(() => initLobbyPolling(), [])

  return ownFriendCode ? (
    <FriendsScreen ownFriendCode={ownFriendCode} />
  ) : (
    <FriendsScreen />
  )
}
